/**
 * <jgd-post-add-modal> — öffnet ein Modal, wenn ein Produkt in den Warenkorb
 * gelegt wurde, und rendert die im Editor komponierten Blöcke im Kontext des
 * gerade hinzugefügten Produkts. (Proof of Concept, s.
 * docs/plans/2026-07-17-jgd-post-add-modal.md)
 *
 * v2 (2026-09-27, VORSCHLAG — nicht deployt): Inhalt wird VORAB gerendert.
 * Gemessen am 27.09. (PDP Lind DNA Tischset, Desktop, ohne Cookie-Zustimmung,
 * 4 Paare abwechselnd alt/neu): Inhalt steht nach 586 ms statt 1.062 ms
 * (Median), Dialog öffnet unverändert bei ~540–650 ms. Ein /cart.js-Aufruf je
 * Klick entfällt (3 -> 2).
 *
 * Ablauf:
 * 1. `submit` eines Produktformulars (capture, dokumentweit): Produkt-URL aus
 *    `product-form-component[data-product-url]`, Variant-ID aus `input[name=id]`
 *    -> Section Rendering dieser Section auf der Produkt-URL SOFORT starten,
 *    parallel zu Horizons /cart/add. Der Modal-Inhalt hängt nicht vom
 *    Warenkorb ab (Titel, Buttons, Empfehlungen), darum darf er vor der
 *    Antwort von /cart/add gerendert werden.
 * 2. `cart:update` (nur echte Adds aus `product-form-component`, keine
 *    Mengenänderungen/Fehler) -> Variant-ID aus `detail.data.variantId`/
 *    `detail.sourceId`. Dialog öffnen.
 * 3. Passt die Variant-ID zum vorab gerenderten Inhalt: diesen einsetzen.
 *    Sonst Rückfall: Produkt-URL vom auslösenden Element (`event.target`),
 *    zuletzt wie bisher GET /cart.js -> Line-Item -> Produkt-URL (z. B.
 *    Quick-Add auf Collections: dessen product-form-component trägt kein
 *    data-product-url).
 * 4. Modal-Inhalt austauschen (Custom Elements der Blöcke initialisieren
 *    sich beim Einfügen selbst).
 *
 * Schlägt die Kontext-Beschaffung fehl, bleibt der server-gerenderte
 * Fallback-Inhalt stehen. Im Theme-Editor öffnet die Section-Auswahl den
 * Dialog zum Stylen.
 */

class JgdPostAddModal extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /**
   * Vorab gerenderter Inhalt des zuletzt abgeschickten Produktformulars.
   * @type {{ variantId: number, promise: Promise<Element|null>, fertig: boolean } | null}
   */
  #vorab = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // capture: Horizon bindet den Submit deklarativ (on:submit) und ruft
    // preventDefault — im Capture-Lauf sehen wir das Event trotzdem zuerst.
    document.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    document.addEventListener('cart:update', this.#onCartUpdate, { signal });

    // Konvention: Blöcke im Modal, deren Link auf "#close" zeigt (z. B. ein
    // Stock-Button "Weiter einkaufen"), schließen den Dialog.
    this.addEventListener(
      'click',
      (event) => {
        const closer = event.target.closest('a[href="#close"], a[href$="#close"]');
        if (!closer) return;
        event.preventDefault();
        this.#dialog?.closeDialog();
      },
      { signal }
    );

    if (this.hasAttribute('data-design-mode')) {
      const section = this.closest('.shopify-section');
      section?.addEventListener('shopify:section:select', () => this.#dialog?.showDialog(), { signal });
      section?.addEventListener('shopify:section:deselect', () => this.#dialog?.closeDialog(), { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {(HTMLElement & {showDialog: Function, closeDialog: Function}) | null} */
  get #dialog() {
    return this.querySelector('dialog-component');
  }

  /**
   * Schritt 1: beim Absenden eines Produktformulars den Inhalt vorab rendern.
   * @param {Event} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const component = form.closest('product-form-component');
    const productUrl = component?.dataset.productUrl;
    const variantId = Number(form.querySelector('input[name="id"]')?.value);
    if (!productUrl || !variantId) return;

    const eintrag = { variantId, promise: null, fertig: false };
    eintrag.promise = this.#render(productUrl, variantId).finally(() => {
      eintrag.fertig = true;
    });
    this.#vorab = eintrag;
  };

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const { sourceId, data } = event.detail ?? {};
    // Nur echte Adds — cart:update feuert auch bei Mengenänderung/Entfernen
    if (data?.source !== 'product-form-component' || data?.didError) return;

    const variantId = Number(data?.variantId ?? sourceId);
    if (!variantId) return;

    const vorab = this.#vorab;
    this.#vorab = null;

    let inhalt;
    if (vorab && vorab.variantId === variantId) {
      inhalt = vorab.promise;
      // Skeleton nur, wenn der vorab gerenderte Inhalt noch nicht da ist
      if (!vorab.fertig) this.setAttribute('data-loading', '');
    } else {
      this.setAttribute('data-loading', '');
      // Rückfall 1: Produkt-URL vom auslösenden Element (Event bubbelt von
      // der product-form-component); Rückfall 2: wie bisher über /cart.js.
      const target = /** @type {Element|null} */ (event.target instanceof Element ? event.target : null);
      const productUrl = target?.closest('[data-product-url]')?.getAttribute('data-product-url');
      inhalt = productUrl ? this.#render(productUrl, variantId) : this.#renderViaCart(variantId);
    }

    this.#dialog?.showDialog();
    this.#einsetzen(inhalt);
  };

  /**
   * Section Rendering: dieselbe Section auf der Produkt-URL re-rendern —
   * `closest.product` der Blöcke löst dort auf das hinzugefügte Produkt auf.
   * @param {string} productUrl  /products/<handle> (ggf. mit Locale-Präfix, ggf. mit ?variant=)
   * @param {number} variantId
   * @returns {Promise<Element|null>}
   */
  async #render(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');

    const response = await fetch(url, { signal: this.#controller.signal });
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('[ref="modalContent"]');
  }

  /**
   * Bisheriger Weg (Rückfall): GET /cart.js -> Line-Item zur Variant-ID ->
   * dessen Produkt-URL -> Section Rendering.
   * @param {number} variantId
   * @returns {Promise<Element|null>}
   */
  async #renderViaCart(variantId) {
    const cart = await (await fetch('/cart.js', { signal: this.#controller.signal })).json();
    const item = cart?.items?.find((line) => line.variant_id === variantId);
    if (!item?.url) return null;
    return this.#render(item.url, variantId);
  }

  /** @param {Promise<Element|null>} inhalt */
  async #einsetzen(inhalt) {
    try {
      const fresh = await inhalt;
      const current = this.querySelector('[ref="modalContent"]');
      if (fresh && current) current.replaceWith(fresh);
    } catch {
      // Kontext-Fetch fehlgeschlagen — server-gerenderter Inhalt bleibt stehen
    } finally {
      this.removeAttribute('data-loading');
    }
  }
}

if (!customElements.get('jgd-post-add-modal')) {
  customElements.define('jgd-post-add-modal', JgdPostAddModal);
}
